import { CheckCircle2, Lock, Bell, ChevronRight, PlayCircle, BookOpen } from "lucide-react";
import { cn } from "@/lib/utils";
import { Pillar, PILLAR_META } from "@/components/premium/PillarBadge";
import { CoursePhaseConfig, getStatusBadgeColor } from "@/lib/curriculumPhases";

type Status = "completed" | "in_progress" | "not_started" | "locked";

interface MilestoneCardProps {
  title: string;
  pillar: Pillar;
  description?: string;
  completed: number;
  total: number;
  status: Status;
  lockedConfig?: CoursePhaseConfig;
  onClick: () => void;
}

const PILLAR_RING: Record<Pillar, string> = {
  finance: "before:bg-primary",
  economics: "before:bg-accent",
  entrepreneurship: "before:bg-secondary",
};

const PILLAR_GLOW: Record<Pillar, string> = {
  finance: "hover:shadow-[0_0_28px_hsl(var(--primary)/0.18)]",
  economics: "hover:shadow-[0_0_28px_hsl(var(--accent)/0.18)]",
  entrepreneurship: "hover:shadow-[0_0_28px_hsl(var(--secondary)/0.18)]",
};

export default function MilestoneCard({
  title,
  pillar,
  description,
  completed,
  total,
  status,
  lockedConfig,
  onClick,
}: MilestoneCardProps) {
  const meta = PILLAR_META[pillar];
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  const isLocked = status === "locked";
  const isComplete = status === "completed";
  const isProgress = status === "in_progress";

  const Icon = isLocked ? Lock : isComplete ? CheckCircle2 : isProgress ? PlayCircle : BookOpen;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "group relative w-full text-left rounded-2xl border bg-card/70 backdrop-blur-xl",
        "transition-all duration-200 overflow-hidden",
        "before:absolute before:left-0 before:top-0 before:bottom-0 before:w-[3px]",
        PILLAR_RING[pillar],
        isLocked
          ? "border-border/40 opacity-75 hover:opacity-95"
          : isComplete
          ? "border-emerald-500/30 hover:border-emerald-500/50"
          : "border-border/60 hover:border-border",
        !isLocked && PILLAR_GLOW[pillar],
        "p-4",
      )}
    >
      <div className="flex items-start gap-3">
        <div
          className={cn(
            "h-9 w-9 shrink-0 rounded-xl flex items-center justify-center border",
            isLocked
              ? "bg-muted/40 border-border/40 text-muted-foreground"
              : isComplete
              ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-400"
              : pillar === "finance"
              ? "bg-primary/10 border-primary/30 text-primary"
              : pillar === "economics"
              ? "bg-accent/10 border-accent/30 text-accent"
              : "bg-secondary/10 border-secondary/30 text-secondary-foreground",
          )}
        >
          <Icon className="w-4 h-4" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span
              className={cn(
                "text-[9px] font-heading font-bold uppercase tracking-wider",
                meta.tone,
              )}
            >
              {meta.label}
            </span>
            <span className="text-[9px] text-muted-foreground/70">·</span>
            <span className="text-[9px] text-muted-foreground truncate">
              {meta.instructor}
            </span>
          </div>

          <p
            className={cn(
              "text-sm font-heading font-semibold leading-snug line-clamp-2",
              isLocked ? "text-foreground/70" : "text-foreground",
            )}
          >
            {title}
          </p>

          {isLocked && lockedConfig ? (
            <div className="mt-2 flex items-center gap-2">
              <span
                className={cn(
                  "inline-flex items-center rounded-full border px-1.5 py-0 text-[9px] font-bold uppercase tracking-wider",
                  getStatusBadgeColor(lockedConfig.status),
                )}
              >
                {lockedConfig.badge}
              </span>
              <Bell className="w-3 h-3 text-muted-foreground/60" />
            </div>
          ) : (
            <div className="mt-2.5">
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1 rounded-full bg-muted/60 overflow-hidden">
                  <div
                    className={cn(
                      "h-full rounded-full transition-all",
                      isComplete
                        ? "bg-emerald-400"
                        : pillar === "finance"
                        ? "bg-primary"
                        : pillar === "economics"
                        ? "bg-accent"
                        : "bg-secondary",
                    )}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="text-[10px] font-mono text-muted-foreground shrink-0">
                  {completed}/{total}
                </span>
              </div>
              <div className="mt-1.5 flex items-center justify-between">
                <span
                  className={cn(
                    "text-[10px] font-semibold uppercase tracking-wider",
                    isComplete
                      ? "text-emerald-400"
                      : isProgress
                      ? meta.tone
                      : "text-muted-foreground",
                  )}
                >
                  {isComplete
                    ? "Completed"
                    : isProgress
                    ? "Continue"
                    : "Start"}
                </span>
                <ChevronRight
                  className={cn(
                    "w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5",
                    isComplete ? "text-emerald-400" : "text-muted-foreground",
                  )}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </button>
  );
}